import JsBarcode from "jsbarcode";

import type { LabelFormat } from "./racketData";

export interface LabelPosition {
  page: number;
  topMm: number;
  leftMm: number;
}

export const cellsPerSheet = (format: LabelFormat): number =>
  format.columns * format.rows;

// Where each label goes on the A4 sheets of adhesive labels: cells are
// numbered from 1, left to right and top to bottom; startCell skips the
// cells already used on a sheet begun earlier. Labels that do not fit go on
// the next sheets, from the first cell.
export const labelPositions = (
  format: LabelFormat,
  count: number,
  startCell = 1,
): LabelPosition[] => {
  const perSheet = cellsPerSheet(format);
  const first = Math.min(Math.max(1, Math.floor(startCell)), perSheet) - 1;
  return Array.from({ length: count }, (_, index) => {
    const cell = first + index;
    const inSheet = cell % perSheet;
    return {
      page: Math.floor(cell / perSheet),
      topMm:
        format.margin_top_mm +
        Math.floor(inSheet / format.columns) * format.label_height_mm,
      leftMm:
        format.margin_left_mm +
        (inSheet % format.columns) * format.label_width_mm,
    };
  });
};

export interface LabelContent {
  code: string;
  model: string;
}

const A4_HEIGHT_MM = 297;

// Prints labels on A4 sheets of adhesive labels (app_settings
// .test_racket_label_format), from startCell on; labels that do not fit go
// on the next sheets. The page opens in a new window built here with DOM
// calls (no HTML strings), with only the labels in it.
export const printLabels = (
  labels: LabelContent[],
  format: LabelFormat,
  startCell = 1,
) => {
  const win = window.open("", "_blank", "width=800,height=600");
  if (!win) return;
  const doc = win.document;
  doc.title = labels.map((label) => label.code).join(", ");
  const style = doc.createElement("style");
  style.textContent = `
    @page { size: A4; margin: 0; }
    body { margin: 0; font-family: sans-serif; }
    .sheet { position: relative; width: 210mm; height: ${A4_HEIGHT_MM}mm; overflow: hidden; }
    .sheet + .sheet { break-before: page; }
    .label {
      position: absolute;
      width: ${format.label_width_mm}mm; height: ${format.label_height_mm}mm;
      box-sizing: border-box; padding: 3mm;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      gap: 1mm; overflow: hidden;
    }
    .label svg { max-width: 100%; height: auto; }
    .model { font-size: 9pt; text-align: center; }
  `;
  doc.head.appendChild(style);
  const sheets: HTMLDivElement[] = [];
  labelPositions(format, labels.length, startCell).forEach(
    (position, index) => {
      while (sheets.length <= position.page) {
        const sheet = doc.createElement("div");
        sheet.className = "sheet";
        doc.body.appendChild(sheet);
        sheets.push(sheet);
      }
      const svg = doc.createElementNS("http://www.w3.org/2000/svg", "svg");
      JsBarcode(svg, labels[index].code, {
        format: "CODE128",
        height: 50,
        margin: 0,
        fontSize: 16,
      });
      const label = doc.createElement("div");
      label.className = "label";
      label.style.top = `${position.topMm}mm`;
      label.style.left = `${position.leftMm}mm`;
      label.appendChild(svg);
      const caption = doc.createElement("div");
      caption.className = "model";
      caption.textContent = labels[index].model;
      label.appendChild(caption);
      sheets[position.page].appendChild(label);
    },
  );
  win.focus();
  win.print();
};
